import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { receivedAttachments, type ReceivedAttachment } from "../lib/api";

export default function ReceivedAttachments({ messageId }: { messageId: string }) {
    const [downloading, setDownloading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const query = useQuery({ queryKey: ["received-attachments", messageId], queryFn: () => receivedAttachments.list(messageId), staleTime: 5 * 60_000, retry: 1 });
    async function download(part: ReceivedAttachment) {
        setDownloading(part.id);
        setError(null);
        try {
            const blob = await receivedAttachments.download(messageId, part.id);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = part.filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch { setError("Could not download the attachment. Please try again."); }
        finally { setDownloading(null); }
    }
    if (query.isPending) return <p role="status">Checking for attachments…</p>;
    if (query.isError) return <p role="alert">Could not load attachments. <button type="button" onClick={() => void query.refetch()}>Retry</button></p>;
    if (!query.data.attachments.length) return null;
    return <section className="received-attachments" aria-label="Email attachments">
        <h4>Attachments</h4>
        <ul>{query.data.attachments.map(part => <li key={part.id}>
            <button type="button" disabled={downloading !== null} onClick={() => void download(part)}>
                {downloading === part.id ? "Downloading…" : part.filename}
            </button>
            <span> {part.size < 1024 ? `${part.size} bytes` : `${Math.ceil(part.size / 1024)} KB`}</span>
        </li>)}</ul>
        {error && <p role="alert">{error}</p>}
    </section>;
}
